// @flow

import React, { type Node } from 'react';
import styled from 'styled-components';

import {
  useIncomingPayment,
  getMerchant,
  getRedirectUrlAfterPayment,
  getCountry,
} from '@tectonics/prs/incoming-payment';

import { getName as getMerchantName } from '@tectonics/prs/merchant';
import { Button } from '../../atoms';
import { translate, translateHTML } from '@tectonics/translate';

export function MerchantInfo(): Node {
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);

  switch (country) {
    case 'br':
      return <BrazilianMerchantInfo />;

    case 'ar':
    case 'bo':
    case 'cl':
    case 'co':
    case 'ec':
    case 'mx':
    case 'pe':
    case 'uy':
    case 'py':
    case 'cr':
    case 'pa':
    case 'gt':
    case 'sv':
    case 'do':
      return <LatamMerchantInfo />;

    case 'za':
    case 'ke':
    case 'ng':
    case 'in':
    case 'tz':
    case 'ug':
    case 'zm':
    case 'gh':
    case 'jm':
      return <EnglishMerchantInfo />;

    case 'eg':
      return <ArabicMerchantInfo />;

    default:
      throw new Error(`Merchant info: country ${country} is not supported`);
  }
}

function BrazilianMerchantInfo(): Node {
  const incomingPayment = useIncomingPayment();
  const merchant = getMerchant(incomingPayment);
  const merchantName = getMerchantName(merchant);
  const redirectUrl = getRedirectUrlAfterPayment(incomingPayment);

  return (
    <Root>
      <p>
        Caso deseje, você pode voltar para
        {'\u0020'}
        <strong>{merchantName}</strong>
        {'\u0020'}
        clicando
        no botão abaixo.
      </p>
      <Link as="a" type="primary" flat outline rounded href={redirectUrl}>
        Voltar para {merchantName}
      </Link>
    </Root>
  );
}

function LatamMerchantInfo(): Node {
  const incomingPayment = useIncomingPayment();
  const merchant = getMerchant(incomingPayment);
  const merchantName = getMerchantName(merchant);
  const redirectUrl = getRedirectUrlAfterPayment(incomingPayment);

  return (
    <Root>
      <p>
        Si lo desea, puede volver a
        {'\u0020'}
        <strong>{merchantName}</strong>
        {'\u0020'}
        haciendo clic en el siguiente botón:
      </p>
      <Link as="a" type="primary" flat outline rounded href={redirectUrl}>
        Volver a {merchantName}
      </Link>
    </Root>
  );
}

function EnglishMerchantInfo(): Node {
  const incomingPayment = useIncomingPayment();
  const merchant = getMerchant(incomingPayment);
  const merchantName = getMerchantName(merchant);
  const redirectUrl = getRedirectUrlAfterPayment(incomingPayment);

  return (
    <Root>
      <p>
        If you wish, you can return to
        {'\u0020'}
        <strong>{merchantName}</strong>
        {'\u0020'}
        by clicking on
        the button below.
      </p>
      <Link as="a" type="primary" flat outline rounded href={redirectUrl}>
        Back to {merchantName}
      </Link>
    </Root>
  );
}

function ArabicMerchantInfo(): Node {
  const incomingPayment = useIncomingPayment();
  const merchant = getMerchant(incomingPayment);
  const merchantName = getMerchantName(merchant);
  const redirectUrl = getRedirectUrlAfterPayment(incomingPayment);

  return (
    <Root>
      <p>
        {translateHTML('volcanes.opened-payment.fawry.merchantInfo.returnButton', {merchantName})}
      </p>
      <Link as="a" type="primary" flat outline rounded href={redirectUrl}>
        {translate('volcanes.opened-payment.fawry.merchantInfo.backTo', {merchantName})}
      </Link>
    </Root>
  );
}

const Link = styled(Button)`
  margin: 2em auto 0;
`;

const Root = styled.div`
  padding: 32px;
  color: ${props => props.theme.colors.neutral[900]};
  background-color: ${props => props.theme.colors.new_primary[50]};
  text-align: center;
`;
